import { AbstractNode } from '@/orrm/model/index'
import TextProcessed from '@/orrm/TextProcessed'
import { Builder, ToManyRelation } from 'coloquent'
import { Phenomene } from '@/orrm/model/phenomene'
import { Thematique } from '@/orrm/model/thematique'
import { Public } from '@/orrm/model/public'
import { EsriJSON, GeoJSON, GPX, KML, TopoJSON, WFS, WMSCapabilities, WMTSCapabilities } from 'ol/format'
import OSMXML from 'ol/format/OSMXML'
import GML3 from 'ol/format/GML3'
import GML32 from 'ol/format/GML32'
import GML2 from 'ol/format/GML2'
import axios from 'axios'
import XML from 'ol/format/XML'
import Link from '@/orrm/Link'
import { Feature } from 'ol'
import { Geometry } from 'ol/geom'

export type LayerType = 'WMS' | 'WMTS' | 'WFS' | 'fake'

export type LayerRubric = 'info_reglementation' | 'actualites' | 'entites' | 'donnees'

export type FeatureFormat = 'GeoJSON' | 'TopoJSON' | 'EsriJSON' | 'KML' | 'OSMXML' | 'GPX' | 'WFS' | 'GML3' | 'GML32' | 'GML2'

export class Layer extends AbstractNode {
  protected jsonApiType = 'node/orrm_couche_cartographique'
  protected static pageSize = 10
  public static readonly searchField = 'title'
  public cachedFeatures: { [id: string]: Feature<Geometry> } = {}
  public featuresList: Feature<Geometry>[] = []

  static createFakeLayer (attributes: { [key: string]: any }, uuid: string): Layer {
    return Layer.createLayer(attributes, uuid, 'fake')
  }

  static createLayer (attributes: { [key: string]: any }, uuid: string, layerType: LayerType = 'fake'): Layer {
    const layer = new Layer()

    for (const key in attributes) {
      layer.setAttribute(key, attributes[key])
    }

    layer.setAttribute('field_orrm_afficher_la_couche', true)
    layer.setApiId(uuid)
    layer.setAttribute('field_orrm_type_de_couche', layerType)
    layer.setRelation('field_orrm_phenomene', [])
    layer.setRelation('field_orrm_thematique', [])
    layer.setRelation('field_orrm_public', [])
    return layer
  }

  static getQueryBuilder (): Builder<Layer> {
    return Layer
      .with('field_orrm_phenomene')
      .with('field_orrm_thematique')
      .with('field_orrm_public') as Builder<Layer>
  }

  async getCapabilities (): Promise<any> {
    const response = await axios.get(`${this.flux.value}?SERVICE=${this.layerType}&REQUEST=GetCapabilities`, { responseType: 'text' })
    let result = null
    if (this.layerType === 'WMS') {
      const wmsCapabilities = new WMSCapabilities()
      result = wmsCapabilities.read(response.data as string)
    } else if (this.layerType === 'WMTS') {
      const wmtsCapabilities = new WMTSCapabilities()
      result = wmtsCapabilities.read(response.data as string)
    }

    return result
  }

  get OlWFSFormat () {
    if (!this.featureFormat) return undefined
    return {
      GeoJSON,
      TopoJSON,
      EsriJSON,
      KML,
      OSMXML,
      GPX,
      WFS,
      GML3,
      GML32,
      GML2
    }[this.featureFormat]
  }

  get title (): string {
    return this.getAttribute('title')
  }

  get isDefault (): boolean {
    return this.getAttribute('field_orrm_affichage_defaut')
  }

  get show (): boolean {
    return this.getAttribute('field_orrm_afficher_la_couche')
  }

  get resolutionMin (): number {
    return this.getAttribute('field_orrm_resolution_min')
  }

  get resolutionMax (): number {
    return this.getAttribute('field_orrm_resolution_max')
  }

  get layerType (): LayerType {
    return this.getAttribute('field_orrm_type_de_couche')
  }

  get flux (): TextProcessed {
    return this.getAttribute('field_orrm_flux')
  }

  get country (): string {
    return this.getAttribute('field_orrm_pays')
  }

  get params (): string {
    return this.getAttribute('field_orrm_parametres')
  }

  get sld (): string | null {
    return this.getAttribute('field_orrm_sld')
  }

  get featureFormat (): FeatureFormat | null {
    return this.getAttribute('field_orrm_feature_format')
  }

  get link (): Link | null {
    return this.getAttribute('field_orrm_lien')
  }

  get linkData (): Link | null {
    return this.getAttribute('field_lien_donnees')
  }

  get rubric (): LayerRubric | null {
    return this.getAttribute('field_orrm_rubrique')
  }

  field_orrm_phenomene (): ToManyRelation<Phenomene, this> { // eslint-disable-line
    return this.hasMany(Phenomene)
  }

  field_orrm_thematique (): ToManyRelation<Thematique, this> { // eslint-disable-line
    return this.hasMany(Thematique)
  }

  field_orrm_public (): ToManyRelation<Public, this> { // eslint-disable-line
    return this.hasMany(Public)
  }

  getPhenomenes (): Phenomene[] {
    return this.getRelation('field_orrm_phenomene')
  }

  getThematiques (): Thematique[] {
    return this.getRelation('field_orrm_thematique')
  }

  getPublics (): Public[] {
    return this.getRelation('field_orrm_public')
  }
}
